import { Progress } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { MyIcon } from '~/components/ui/MyIcon'

import {
  AiArtWorkStatus,
  AiArtworkServiceStatus,
  AiCreateArtworkPattern,
  getAIMemberList_accountModule_userMemberCardList,
} from '~/graphqls/types'
import { to400Image } from '~/utils'

import styles from './QuickPreview.module.css'
import { observer } from 'mobx-react'
import classNames from 'classnames'
import useUpdateList from '~/hooks/ai/useUpdateList'
import { QuickTool } from './QuickTool'
import { ExtendsProduct } from '~/graphqls/types'
import { useStores } from '~/hooks/useStores'
import usePreviewData from '~/hooks/ai/usePreivewData'
import { AI_QUICK_PAGE_SIZE } from '~/configs'
import { MyPagination } from '~/components/ui/MyPagination'
import { QuickBigPic } from '~/components/pages/ai/QuickBigPic'
import { setAgainParams } from './utils'

interface Props {
  memberList: getAIMemberList_accountModule_userMemberCardList[]
  /** 锁定pattern */
  lockPattern?: AiCreateArtworkPattern
}

const QuickPreview = ({
  memberList = [],
  lockPattern = AiCreateArtworkPattern.GENERAL,
}: Props) => {
  const { store } = useStores()

  const [pageInfo, setPageInfo] = useState({
    page: 1,
    size: AI_QUICK_PAGE_SIZE,
  })

  const {
    data,
    total,
    fetchData: refreshPreviewData,
  } = usePreviewData(lockPattern)
  const [product, setProduct] = useState<ExtendsProduct>(data[0])

  // 轮询，更新进度
  const progressCache = useUpdateList<ExtendsProduct>(data)

  const newData = useMemo(() => {
    return data.map(item => {
      if (progressCache[item.id]) {
        return {
          ...item,
          ...progressCache[item.id],
        }
      }

      return item
    })
  }, [data, progressCache])

  const handleSelectPic = (item: ExtendsProduct) => {
    setProduct(item)
    setAgainParams(item)

    store.ai?.setIsAgain(true)
  }

  /** 分页 */
  const onPageChange = (pageNumber: number) => {
    setPageInfo(pageInfo => ({
      ...pageInfo,
      page: pageNumber,
    }))

    refreshPreviewData({
      page: pageNumber,
    })
  }

  /** 更新当前作品 */
  useEffect(() => {
    if (!newData.length) return

    /** 点击生成后，需要默认选中第一个 */
    if (store.ai?.isResetSelectProduct) {
      setProduct(newData[0])
      store.ai.setIsResetSelectProduct(false)
      return
    }

    const productIndex = newData.findIndex(item => item.id === product?.id)

    if (product?.id && productIndex > -1) {
      setProduct(newData[productIndex])
    } else {
      setProduct(newData[0])
    }
  }, [newData])

  useEffect(() => {
    if (store.ai?.isRefeshPreviewData) {
      setPageInfo(pageInfo => ({
        ...pageInfo,
        page: 1,
      }))
      refreshPreviewData({
        page: 1,
      }).finally(() => {
        store.ai?.setIsRefeshPreviewData(false)
      })
    }
  }, [store.ai?.isRefeshPreviewData])

  return (
    <div className="flex justify-end items-start">
      <div className={classNames([product ? 'w-[584px]' : '!w-[632px]'])}>
        <div className="w-full overflow-hidden flex-col items-center justify-center">
          <QuickBigPic product={product} data={newData} />
          <div className="w-full h-3 bg-neutral-100"></div>
        </div>

        {/* 预览图 */}
        <div className="flex flex-wrap justify-start gap-1 mb-4">
          {newData.slice(0, AI_QUICK_PAGE_SIZE).map(item => {
            return (
              <div
                key={item.id}
                className={classNames(
                  'w-[143px] h-[143px] cursor-pointer rounded-lg overflow-hidden relative',
                  {
                    'border-2 border-solid border-neutral-500':
                      product?.id === item.id,
                  },
                  styles.item
                )}
                onClick={() => {
                  handleSelectPic(item)
                }}
              >
                {!!item.miniArtWorkUrl && (
                  <>
                    {item.serviceStatus === AiArtworkServiceStatus.CLEANED ? (
                      <div
                        className="rounded-lg bg-neutral-200 flex flex-col items-center
                justify-center h-full w-full"
                      >
                        <MyIcon
                          icon="yijianyidu"
                          className="text-neutral-500 text-[24px]"
                        />
                        <div className="text-neutral-400 text-xs mt-2 text-center">
                          已清理
                        </div>
                      </div>
                    ) : (
                      <>
                        {!!item.involveYellow ? (
                          <div
                            className="rounded-lg bg-neutral-200 flex flex-col items-center
                justify-center h-full w-full"
                          >
                            <MyIcon
                              icon="warning"
                              className="text-neutral-500 text-[24px]"
                            />
                            <div className="text-neutral-400 text-xs mt-2 text-center">
                              图片涉嫌违规
                            </div>
                          </div>
                        ) : (
                          <img
                            src={to400Image(item?.miniArtWorkUrl)}
                            alt=""
                            className="w-[143px] h-[143px] mb-1 rounded-lg object-cover"
                          />
                        )}
                      </>
                    )}
                  </>
                )}
                {!item.miniArtWorkUrl && (
                  <div
                    className="rounded-lg bg-neutral-200 flex flex-col items-center
              justify-center h-full w-full"
                  >
                    {[AiArtWorkStatus.FAIL, AiArtWorkStatus.CANCEL].includes(
                      item.status!
                    ) && (
                      <div className="w-6 h-6">
                        <MyIcon
                          icon={
                            [
                              AiArtWorkStatus.FAIL,
                              AiArtWorkStatus.CANCEL,
                            ].includes(item.status!)
                              ? 'file-error'
                              : 'file'
                          }
                          className="text-lg text-neutral-400"
                        />
                      </div>
                    )}
                    <div className="text-neutral-400 text-xs mt-2 text-center">
                      {item.status === AiArtWorkStatus.FAIL ? (
                        '生成失败'
                      ) : item.status === AiArtWorkStatus.CANCEL ? (
                        '已撤销'
                      ) : (
                        <div>
                          <div>
                            {(Number(item?.completePercent || 0) * 100).toFixed(
                              2
                            )}
                            %
                          </div>
                          <div>正在生成中 </div>
                          <div className="w-[77px] mx-auto">
                            <Progress
                              strokeWidth={6}
                              showInfo={false}
                              percent={Number(
                                (item?.completePercent || 0) * 100
                              )}
                              strokeColor="#7851EC"
                              trailColor="#353945"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <MyPagination
          hideOnSinglePage
          showTotal={total => `共 ${total} 项数据`}
          showSizeChanger={false}
          pageSize={pageInfo.size}
          current={pageInfo.page}
          total={total}
          onChange={onPageChange}
          size="small"
          showLessItems={true}
        />
      </div>
      {/* 快捷工具 */}
      {product && (
        <div className="flex-1 ml-6">
          <QuickTool
            product={product}
            setProduct={setProduct}
            memberList={memberList}
            lockPattern={lockPattern}
          />
        </div>
      )}
    </div>
  )
}

export default observer(QuickPreview)
